import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingBreakdown = ({ pricing, selectedDates, onBookNow }) => {
  const [rentalDays, setRentalDays] = useState(0);
  const [subtotal, setSubtotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (selectedDates?.start && selectedDates?.end) {
      const days = Math.ceil((selectedDates?.end - selectedDates?.start) / (1000 * 60 * 60 * 24)) + 1;
      setRentalDays(days);

      let calculatedSubtotal = 0;
      let calculatedDiscount = 0;

      if (days >= 7) {
        calculatedSubtotal = days * pricing?.dailyRate;
        calculatedDiscount = calculatedSubtotal * (pricing?.weeklyDiscount / 100);
      } else {
        calculatedSubtotal = days * pricing?.dailyRate;
      }

      setSubtotal(calculatedSubtotal);
      setDiscount(calculatedDiscount);
      setTotal(calculatedSubtotal - calculatedDiscount + pricing?.securityDeposit + pricing?.cleaningFee);
    } else {
      setRentalDays(0);
      setSubtotal(0);
      setDiscount(0);
      setTotal(0);
    }
  }, [selectedDates, pricing]);

  return (
    <div className="bg-card rounded-lg shadow-elevation-3 p-4 md:p-6 lg:p-8 sticky top-24">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <span className="font-heading text-3xl md:text-4xl font-bold text-foreground">
            ${pricing?.dailyRate}
          </span>
          <span className="font-body text-base md:text-lg text-muted-foreground ml-2">
            per day
          </span>
        </div>
        {pricing?.weeklyDiscount > 0 && (
          <div className="px-3 py-1 bg-success/10 text-success rounded-md">
            <span className="font-caption text-sm font-medium">
              {pricing?.weeklyDiscount}% off weekly
            </span>
          </div>
        )}
      </div>
      {rentalDays > 0 && (
        <div className="space-y-4 mb-6 pb-6 border-b border-border">
          <div className="flex justify-between items-center">
            <span className="font-body text-sm md:text-base text-muted-foreground">
              ${pricing?.dailyRate} × {rentalDays} days
            </span>
            <span className="font-body text-sm md:text-base font-medium text-foreground">
              ${subtotal?.toFixed(2)}
            </span>
          </div>

          {discount > 0 && (
            <div className="flex justify-between items-center">
              <span className="font-body text-sm md:text-base text-success">
                Weekly discount ({pricing?.weeklyDiscount}%)
              </span>
              <span className="font-body text-sm md:text-base font-medium text-success">
                -${discount?.toFixed(2)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="font-body text-sm md:text-base text-muted-foreground">
              Security Deposit
            </span>
            <span className="font-body text-sm md:text-base font-medium text-foreground">
              ${pricing?.securityDeposit?.toFixed(2)}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="font-body text-sm md:text-base text-muted-foreground">
              Cleaning Fee
            </span>
            <span className="font-body text-sm md:text-base font-medium text-foreground">
              ${pricing?.cleaningFee?.toFixed(2)}
            </span>
          </div>
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <span className="font-heading text-xl md:text-2xl font-semibold text-foreground">
          Total
        </span>
        <span className="font-heading text-xl md:text-2xl font-bold text-primary">
          ${rentalDays > 0 ? total?.toFixed(2) : '0.00'}
        </span>
      </div>
      <Button
        variant="default"
        size="lg"
        fullWidth
        iconName="Calendar"
        iconPosition="left"
        onClick={onBookNow}
        disabled={rentalDays === 0}
      >
        {rentalDays > 0 ? 'Book Now' : 'Select Dates to Book'}
      </Button>
      <div className="mt-6 space-y-3">
        <div className="flex items-start space-x-2">
          <Icon name="Shield" size={18} color="var(--color-success)" strokeWidth={2} />
          <p className="font-body text-sm text-muted-foreground">
            Security deposit fully refundable
          </p>
        </div>
        <div className="flex items-start space-x-2">
          <Icon name="Clock" size={18} color="var(--color-success)" strokeWidth={2} />
          <p className="font-body text-sm text-muted-foreground">
            Free cancellation up to 24 hours before pickup
          </p>
        </div>
        <div className="flex items-start space-x-2">
          <Icon name="CheckCircle2" size={18} color="var(--color-success)" strokeWidth={2} />
          <p className="font-body text-sm text-muted-foreground">
            Insurance coverage included
          </p>
        </div>
      </div>
      {pricing?.additionalFees && pricing?.additionalFees?.length > 0 && (
        <div className="mt-6 pt-6 border-t border-border">
          <h4 className="font-heading text-base font-semibold text-foreground mb-3">
            Additional Fees
          </h4>
          <div className="space-y-2">
            {pricing?.additionalFees?.map((fee, index) => (
              <div key={index} className="flex justify-between items-center">
                <span className="font-body text-sm text-muted-foreground">
                  {fee?.name}
                </span>
                <span className="font-body text-sm font-medium text-foreground">
                  ${fee?.amount?.toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PricingBreakdown;